/**
 * useMediaGallery — Shared hook for media loading, filtering, sorting, grouping.
 * Used by MediaPickerModal (modal) and Gallery page (full page).
 */

import { useState, useMemo } from 'react'
import api from '../services/api'

export interface MediaItem {
  id: string
  mediaUrl: string
  thumbnailUrl: string
  mediaType: 'photo' | 'video'
  onRecordCode?: string
  location?: { city?: string; state?: string; country?: string }
  createdAt: number | string
}

export interface MediaGroup {
  date: string
  location: string
  items: MediaItem[]
}

function normalizeMedia(raw: any[]): MediaItem[] {
  return raw.map(m => ({
    id: m.id,
    mediaUrl: m.file_url || m.media_url || m.mediaUrl,
    thumbnailUrl: m.thumbnail_url || m.thumbnailUrl || m.file_url || m.media_url || m.mediaUrl,
    mediaType: m.media_type || m.mediaType || 'photo',
    onRecordCode: m.onrecord_code || m.onRecordCode,
    location: m.location_data
      ? (typeof m.location_data === 'string' ? JSON.parse(m.location_data) : m.location_data)
      : m.location || undefined,
    createdAt: m.created_at || m.createdAt || Date.now(),
  }))
}

export type MediaFilter = 'all' | 'photo' | 'video'
export type MediaSort = 'newest' | 'oldest'
export type MediaGroupBy = 'date-location' | 'location' | 'date' | 'none'

export function useMediaGallery() {
  const [media, setMedia] = useState<MediaItem[]>([])
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState<MediaFilter>('all')
  const [sort, setSort] = useState<MediaSort>('newest')
  const [groupBy, setGroupBy] = useState<MediaGroupBy>('date-location')
  const [locationFilter, setLocationFilter] = useState('')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())

  const loadMedia = async (source: 'private' | 'shared') => {
    setLoading(true)
    try {
      const result = source === 'private' ? await api.getPrivateMedia() : await api.getSharedMedia()
      if (result.success && result.media) {
        setMedia(normalizeMedia(result.media))
      } else {
        setMedia([])
      }
    } catch {
      setMedia([])
    } finally {
      setLoading(false)
    }
  }

  const filtered = useMemo(() => {
    return media
      .filter(m => filter === 'all' || m.mediaType === filter)
      .filter(m => !locationFilter || m.location?.city === locationFilter)
      .sort((a, b) => sort === 'newest'
        ? new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        : new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )
  }, [media, filter, locationFilter, sort])

  const groups = useMemo(() => {
    const result: Record<string, MediaGroup> = {}

    if (groupBy === 'none') {
      result['all'] = { date: '', location: '', items: filtered }
    } else {
      filtered.forEach(m => {
        const date = new Date(m.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
        const loc = m.location?.city
          ? m.location.state ? `${m.location.city}, ${m.location.state}` : m.location.city
          : 'Unknown location'

        let key: string
        if (groupBy === 'date-location') key = `${date}__${loc}`
        else if (groupBy === 'location') key = loc
        else key = date

        if (!result[key]) result[key] = { date, location: loc, items: [] }
        result[key].items.push(m)
      })
    }
    return result
  }, [filtered, groupBy])

  const locations = useMemo(() => {
    return [...new Set(media.map(m => m.location?.city).filter(Boolean))] as string[]
  }, [media])

  return {
    media,
    loading,
    filter, setFilter,
    sort, setSort,
    groupBy, setGroupBy,
    locationFilter, setLocationFilter,
    expandedGroups, setExpandedGroups,
    filtered,
    groups,
    locations,
    loadMedia,
  }
}
